import i18n from '@/i18n';

/**
 * `Intl` formatting keyed on the active UI language, so a date, timestamp or
 * number reads the way the viewer expects rather than always in one locale.
 * Reads `i18n.language` fresh on every call rather than caching a formatter,
 * since the language can change live (the language switcher, or the
 * profile-preference effect) without a reload.
 */
export function formatDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(date);
}

/** A day/month-only date, for a chart axis tick — `formatDate`'s `dateStyle:
 * 'medium'` (e.g. "24 sep. 2025") is too wide once a day-grain series has
 * more than a handful of points; ticks only need to place a point in time,
 * not fully identify it. */
export function formatShortDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'numeric' }).format(date);
}

export function formatDateTime(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
    date,
  );
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(i18n.language, options).format(value);
}

/**
 * A money amount in the API's own currency (`price_currency`, e.g. Mollie's
 * `SubscriptionProduct.price_currency`) — never a hardcoded currency, the API
 * is the source of truth for what a price is denominated in.
 */
export function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat(i18n.language, { style: 'currency', currency }).format(value);
}
